// Copyright ©️ 2026 eVolve MEP, LLC

import './draft-table.css';

import React, { useEffect, useState } from 'react';

import type { WorkCell } from '@evolvemep/api-ts';
import { Box, Button, Flex } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { GridApi } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { NoRowsOverlay } from 'helpers/ag-grid/NoRowsOverlay';
import { isNotNil } from 'helpers/isNotNil';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';

import { CreateEditWorkCellModal } from './CreateEditWorkCellModal';
import { useWorkCellsGrid } from './useWorkCellsGrid';
import { useWorkCellSetup } from './WorkCellSetupContext';

const NoWorkCellsOverlay = ({ draftExists, api }: { api: GridApi; draftExists: boolean }) =>
  draftExists ? (
    <NoRowsOverlay label="No existing work cells." gridApi={api} />
  ) : (
    <NoRowsOverlay icon="WorkCells" label="Edit setup to get started." gridApi={api} />
  );

export const WorkCellSetupMainPanel = () => {
  const {
    facility: { facilityId, facilityName },
    activeShopConfiguration,
    draftShopConfiguration,
    createDraftShopConfiguration,
    deleteDraftShopConfiguration,
    applyDraftShopConfiguration,
    // Grid props for "draft" config
    serverSideGridProps: {
      agGridProps: draftAgGridProps,
      gridApi: draftGridApi,
      workCellDraggedToEnd: draftWorkCellDraggedToEnd,
      sorting: draftSorting,
    },
    createWorkCell,
    mutationRunning,
  } = useWorkCellSetup();
  const [showDiscard, setShowDiscard] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [creatingWorkCell, setCreatingWorkCell] = useState(false);

  const draftExists = isNotNil(draftShopConfiguration);
  const { registerMessage } = useBlockerWhen('work-cell-setup', draftExists);
  useEffect(
    () =>
      registerMessage(
        'Navigating away will remember your changes in edit mode, but will not reflect in the existing work cell setup.',
      ),
    [registerMessage],
  );
  // Grid props for "active" (not draft) config
  const {
    agGridProps,
    workCellDraggedToEnd,
    gridApi,
    sorting: activeSorting,
  } = useWorkCellsGrid(facilityId, 'active', activeShopConfiguration, draftExists);

  return (
    <>
      <BasePageHeader
        title={facilityName}
        parentPage={{
          title: 'Facilities',
          url: '/facilities',
        }}
        spacing="xl"
        topRightComponent={
          draftExists ? (
            <>
              <Button variant="light" disabled={mutationRunning} onClick={() => setShowDiscard(true)}>
                Discard
              </Button>
              <Button disabled={mutationRunning} onClick={() => setShowSave(true)}>
                Save changes
              </Button>
            </>
          ) : (
            <AgGridStyleTooltip
              withArrow
              openDelay={350}
              label="Add new work cells, remove existing work cells, and map tasks to work cells"
            >
              <Button
                variant="light"
                onClick={createDraftShopConfiguration}
                leftSection={<EvolveIcon icon="Edit" />}
                disabled={mutationRunning}
              >
                Edit setup
              </Button>
            </AgGridStyleTooltip>
          )
        }
        bottomRightComponent={
          draftExists ? (
            <Button
              onClick={() => setCreatingWorkCell(true)}
              leftSection={<EvolveIcon icon="Add" size="sm" />}
              variant="light"
              disabled={mutationRunning}
            >
              New work cell
            </Button>
          ) : null
        }
      />
      <Flex style={{ height: '100%' }} gap="sm">
        <Box style={{ height: '100%', width: draftExists ? '50%' : '100%' }}>
          <div
            className={`work-cell-setup ${workCellDraggedToEnd ? 'dragged-to-end' : ''} ${
              draftExists ? 'active-table' : ''
            }`}
            style={{ height: '100%' }}
          >
            <AgGridReact<WorkCell>
              loading={activeSorting}
              {...agGridProps}
              noRowsOverlayComponent={NoWorkCellsOverlay}
              noRowsOverlayComponentParams={{ draftExists }}
            />
          </div>
        </Box>
        {draftExists && (
          <Box style={{ height: '100%', width: '50%' }}>
            <div
              className={`work-cell-setup draft-table ${draftWorkCellDraggedToEnd ? 'dragged-to-end' : ''}`}
              style={{ height: '100%' }}
            >
              <AgGridReact<WorkCell>
                loading={draftSorting}
                {...draftAgGridProps}
                noRowsOverlayComponent={() => <NoRowsOverlay label="Add a work cell." gridApi={gridApi} />}
              />
            </div>
          </Box>
        )}
      </Flex>

      {isNotNil(draftShopConfiguration) && (
        <>
          <WrappedModal
            title="Discard changes"
            opened={showDiscard}
            onConfirm={async () => {
              await deleteDraftShopConfiguration();
            }}
            onClose={() => setShowDiscard(false)}
            confirmationText="Discard"
            buttonColor="red"
          >
            Are you sure you want to discard your changes?
          </WrappedModal>

          <WrappedModal
            title="Save changes"
            opened={showSave}
            onConfirm={async () => {
              await applyDraftShopConfiguration().then(() => {
                notifications.show({
                  title: 'Successfully updated',
                  message: 'New work cell setup changes saved.',
                  color: 'green',
                });
              });
            }}
            onClose={() => setShowSave(false)}
          >
            Are you ready to save your changes? Doing so will rebalance your work cells. This may take some time.
          </WrappedModal>

          <CreateEditWorkCellModal
            opened={creatingWorkCell}
            onClose={() => setCreatingWorkCell(false)}
            onSubmit={async (values) => {
              await createWorkCell(values, draftShopConfiguration.shopConfigurationId).then((res) => {
                refreshGrid(draftGridApi);
                notifications.show({
                  title: 'Successfully created',
                  message: `Created work cell ${res.workCellName}`,
                  color: 'green',
                });
              });
            }}
          />
        </>
      )}
    </>
  );
};
